/**
 * SentryBoundary
 *
 * Description:
 * Sentry error boundary used in two places: around every top level route in
 * App.js, and around each Stripe payment form. Payment gets its own inner
 * boundary because a crash there costs money, and because the route level name
 * would otherwise be the only thing reported.
 *
 * The fallback renders nothing on purpose. The guest interface must not change,
 * so a crash hides the broken part exactly the way components/ErrorBoundary.jsx
 * already does. No error text, no feedback popup.
 *
 * onError also sends the crash to our own log api. This boundary sits inside
 * the app wide boundary in pages/themeLayout.js and stops the error there, so
 * without this call a crash would reach Sentry but never reach the guest log
 * file.
 *
 * @param {string} name      Reported as the component name, for example
 *                           "Checkingin" or "CheckinPayment".
 */

import * as Sentry from "@sentry/react";
import { logError } from "../helpers/error-logger";

const SentryBoundary = ({ name, children }) => (
  <Sentry.ErrorBoundary
    fallback={null}
    onError={(error, componentStack) =>
      logError(error, name, { componentStack }, "COMPONENT_ERROR")
    }
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default SentryBoundary;
